/**
 * KeyRevealModal — Shows the newly generated (or rotated) API key credentials
 * exactly ONCE. The backend never returns the api_secret again.
 *
 * Dual-Key System:
 *   keyId       (mk_id_...)     — Public identifier, safe to store/log
 *   apiSecret   (mk_secret_...) — Private secret, shown only on create/rotate
 *   apiKey      (sk_live_...)   — Legacy single key (deprecated)
 *
 * Props:
 *   open           : boolean
 *   keyId          : string | null   — public identifier (mk_id_...)
 *   apiSecret      : string | null   — private secret (mk_secret_...)
 *   apiKey         : string | null   — legacy key (sk_live_...) — deprecated
 *   webhookSecret  : string | null   — only present if webhook was configured
 *   onClose        : () => void
 */
import React, { useState } from 'react';
import { Modal, Button, Typography, message, Divider, Checkbox } from 'antd';
import {
  CopyOutlined, CheckOutlined, WarningOutlined, BookOutlined,
  GlobalOutlined, IdcardOutlined, LockOutlined, CodeOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.messageos.io';
const EXTERNAL_API_BASE = `${BASE_URL}/api/v1/external`;

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}

export default function KeyRevealModal({ open, keyId, apiSecret, apiKey, webhookSecret, onClose }) {
  const [copiedId, setCopiedId] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedLegacy, setCopiedLegacy] = useState(false);
  const [copiedWh, setCopiedWh] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async (text, setter, label) => {
    if (!text || typeof text !== 'string') return;
    await copyToClipboard(text);
    setter(true);
    message.success(`${label} copied`);
    setTimeout(() => setter(false), 3000);
  };

  const curlBasicAuth = `curl -u "${keyId || 'mk_id_xxx'}:${apiSecret || 'mk_secret_xxx'}" \\
  ${EXTERNAL_API_BASE}/contacts/list`;

  const curlHeaders = `curl -H "x-api-key-id: ${keyId || 'mk_id_xxx'}" \\
     -H "x-api-secret: ${apiSecret || 'mk_secret_xxx'}" \\
  ${EXTERNAL_API_BASE}/contacts/list`;

  const handleClose = () => {
    setCopiedId(false);
    setCopiedSecret(false);
    setCopiedLegacy(false);
    setCopiedWh(false);
    setCopiedCurl(false);
    setSavedConfirmed(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      closable={false}
      maskClosable={false}
      destroyOnClose
      className="key-reveal-modal"
    >
      {/* Warning banner */}
      <div className="warning-banner">
        <WarningOutlined style={{ fontSize: 28, color: '#d48806', marginBottom: 8 }} />
        <h3>Save Your Credentials Now!</h3>
        <p>These credentials will <strong>NOT</strong> be shown again. Store them securely before closing.</p>
      </div>

      {/* ── Recommended: Dual-Key Auth ─────────────────────── */}
      <div className="credential-section-label">
        <Text strong style={{ fontSize: 13, color: '#1890ff' }}>
          ── Recommended: Dual-Key Auth ──
        </Text>
      </div>

      {/* Key ID — public, safe to store */}
      {keyId && (
        <>
          <div className="credential-label">
            <IdcardOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ fontSize: 13 }}>Key ID</Text>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>(public — safe to store in config)</Text>
          </div>
          <div className="key-display" style={{ borderColor: '#91d5ff' }}>
            <code>{keyId}</code>
          </div>
          <Button
            icon={copiedId ? <CheckOutlined /> : <CopyOutlined />}
            size="large"
            block
            onClick={() => handleCopy(keyId, setCopiedId, 'Key ID')}
            style={{ marginBottom: 16, background: copiedId ? '#52c41a' : '#f0f5ff', borderColor: copiedId ? '#52c41a' : '#adc6ff', color: copiedId ? '#fff' : '#1890ff' }}
          >
            {copiedId ? 'Copied!' : 'Copy Key ID'}
          </Button>
        </>
      )}

      {/* API Secret — private, shown once */}
      {apiSecret && (
        <>
          <div className="credential-label">
            <LockOutlined style={{ color: '#ff4d4f' }} />
            <Text strong style={{ fontSize: 13 }}>API Secret</Text>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>(private — store in env vars ONLY)</Text>
          </div>
          <div className="key-display">
            <code>{apiSecret}</code>
          </div>
          <Button
            type="primary"
            icon={copiedSecret ? <CheckOutlined /> : <CopyOutlined />}
            size="large"
            block
            onClick={() => handleCopy(apiSecret, setCopiedSecret, 'API Secret')}
            style={{ marginBottom: 4, background: copiedSecret ? '#52c41a' : undefined, borderColor: copiedSecret ? '#52c41a' : undefined }}
          >
            {copiedSecret ? 'Copied!' : 'Copy API Secret'}
          </Button>
          <Text type="danger" style={{ display: 'block', fontSize: 12, marginBottom: 12, textAlign: 'center' }}>
            ⚠️ Secret shown ONCE — cannot be retrieved later
          </Text>
        </>
      )}

      {/* ── Legacy Single Key (deprecated) ─────────────────── */}
      {apiKey && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div className="credential-section-label legacy">
            <Text strong style={{ fontSize: 13, color: '#8c8c8c' }}>
              ── OR: Legacy Single Key ──
            </Text>
            <span style={{ fontSize: 11, marginLeft: 6, color: '#fa8c16' }}>
              <ExclamationCircleOutlined /> deprecated — will be removed in future
            </span>
          </div>
          <div className="credential-label">
            <LockOutlined style={{ color: '#8c8c8c' }} />
            <Text strong style={{ fontSize: 13, color: '#595959' }}>API Key</Text>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>(legacy sk_live_)</Text>
          </div>
          <div className="key-display legacy">
            <code>{apiKey}</code>
          </div>
          <Button
            icon={copiedLegacy ? <CheckOutlined /> : <CopyOutlined />}
            size="large"
            block
            onClick={() => handleCopy(apiKey, setCopiedLegacy, 'Legacy API Key')}
            style={{ marginBottom: 12, background: copiedLegacy ? '#52c41a' : '#fafafa', borderColor: copiedLegacy ? '#52c41a' : '#d9d9d9', color: copiedLegacy ? '#fff' : '#595959' }}
          >
            {copiedLegacy ? 'Copied!' : 'Copy Legacy API Key'}
          </Button>
        </>
      )}

      {/* Webhook Secret (only if configured) */}
      {typeof webhookSecret === 'string' && webhookSecret && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div className="credential-label">
            <GlobalOutlined style={{ color: '#722ed1' }} />
            <Text strong style={{ fontSize: 13 }}>Webhook Secret</Text>
          </div>
          <div className="key-display" style={{ borderColor: '#d3adf7' }}>
            <code>{webhookSecret}</code>
          </div>
          <Button
            icon={copiedWh ? <CheckOutlined /> : <CopyOutlined />}
            size="large"
            block
            onClick={() => handleCopy(webhookSecret, setCopiedWh, 'Webhook Secret')}
            style={{ marginBottom: 12, background: copiedWh ? '#52c41a' : undefined, borderColor: copiedWh ? '#52c41a' : undefined, color: copiedWh ? '#fff' : undefined }}
          >
            {copiedWh ? 'Copied!' : 'Copy Webhook Secret'}
          </Button>
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            Use this secret to verify webhook signatures via HMAC-SHA256.
          </Text>
        </>
      )}

      {/* ── Quick Start ────────────────────────────────────── */}
      {keyId && apiSecret && (
        <>
          <Divider style={{ margin: '16px 0 12px' }} />
          <div className="credential-section-label">
            <CodeOutlined style={{ color: '#52c41a', marginRight: 6 }} />
            <Text strong style={{ fontSize: 13, color: '#389e0d' }}>Quick Start</Text>
          </div>
          <div className="quickstart-code-block">
            <div className="quickstart-label"># Using Basic Auth (recommended):</div>
            <pre>{curlBasicAuth}</pre>
            <Divider style={{ margin: '8px 0', borderColor: '#333' }} />
            <div className="quickstart-label"># Using headers:</div>
            <pre>{curlHeaders}</pre>
            <Button
              size="small"
              icon={copiedCurl ? <CheckOutlined /> : <CopyOutlined />}
              className="quickstart-copy-btn"
              onClick={() => handleCopy(`${curlBasicAuth}\n\n${curlHeaders}`, setCopiedCurl, 'Curl examples')}
            >
              {copiedCurl ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </>
      )}

      {/* Base URL info */}
      <div className="base-url-info" style={{ marginTop: 12 }}>
        <div className="label">Base URL for API calls</div>
        <div className="url">{EXTERNAL_API_BASE}</div>
      </div>

      {/* Confirmation checkbox */}
      <div style={{ marginTop: 16, padding: '12px 16px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8 }}>
        <Checkbox
          checked={savedConfirmed}
          onChange={(e) => setSavedConfirmed(e.target.checked)}
        >
          <Text strong style={{ fontSize: 13 }}>I have saved these credentials securely</Text>
        </Checkbox>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
        <Button icon={<BookOutlined />} onClick={() => { handleClose(); navigate('/dashboard/api-docs'); }}>
          View API Docs
        </Button>
        <Button type="primary" ghost onClick={handleClose} disabled={!savedConfirmed}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
